import { EditCommandStore } from "./EditCommandStore"; 
import { ISourceReferable } from "./ISourceReferable";
import { SourceReader } from "./SourceReader";
import { IEditEntityCommandStorable, IEditCommand, EditCommand, EditEntityCommand, WriteValueCommand } from "./command";
import { IPropertyPresentable } from "./IPropertyPresentable";
import { IPropertyEditable } from "./IPropertyEditable";
import { PropertyPresenter } from "./PropertyPresenter";
import { PropertyEditor } from "./PropertyEditor";
import { IPropertyPresenterCreatable } from "./IPropertyPresenterCreatable";
import { IPropertyEditorCreatable } from "./IPropertyEditorCreatable";

export abstract class AbstractEditEntityCommandStore<TSource, TEntity> extends EditCommandStore<TSource> implements IEditEntityCommandStorable<TEntity>, ISourceReferable<TEntity>, IPropertyPresenterCreatable<TEntity>, IPropertyEditorCreatable<TEntity> {
  constructor(entity: TEntity | AbstractEditEntityCommandStore<TSource, TEntity>) {
    super((entity instanceof AbstractEditEntityCommandStore) ? entity : []);
    this._entity = (entity instanceof AbstractEditEntityCommandStore)
      ? entity._entity
      : new SourceReader(entity);
  }

  private readonly _entity: SourceReader<TEntity>;
  
  public registerEntityCommand(command: IEditCommand<TEntity>) {
    if (command) {
      this.registerCommand(new EditEntityCommand(s => this.toEntity(s, this._entity._source), command));
    }
  }

  public getSource(): TEntity {
    return this._entity.getSource();
  }

  public isReferedSameSource(instance: ISourceReferable<TEntity>): boolean {
    return (this.getSource() === instance.getSource());
  }

  protected get<T>(get: (s: TEntity) => T, defaultValue: T): T {
    return this._entity.getOrDefault(get, defaultValue);
  }

  protected abstract toEntity(source: TSource, entity: TEntity): TEntity;

  public createPropertyPresenter<T>(get: (s: TEntity) => T): IPropertyPresentable<T> {
    return new PropertyPresenter({
      source: this._entity._source,
      read: get,
    });
  }

  public createPropertyEditor<T>(get: (s: TEntity) => T, set: (s: TEntity, v: T) => void, isSettable?: (s: TEntity) => boolean): IPropertyEditable<T> {
    return new PropertyEditor({
      source: this._entity._source,
      read: get,
      write: v => this.registerEntityCommand(new WriteValueCommand(get, set, v)),
      isWritable: isSettable,
    });
  }

  public createPropertiesEditor<TValue, TValues>(get: (s: TEntity) => TValues, toValue: (vs: TValues) => TValue, set: (s: TEntity, v: TValue) => void, revert: (s: TEntity, vs: TValues) => void, isSettable?: (s: TEntity) => boolean): IPropertyEditable<TValue> {
    return new PropertyEditor({
      source: this._entity._source,
      read: s => toValue(get(s)),
      write: v => this.registerEntityCommand(new EditCommand(get, s => set(s, v), revert)),
      isWritable: isSettable,
    });
  }
}
